import { Alert, AlertDescription } from '@ophelos/ui/components/alert';
import { Button } from '@ophelos/ui/components/button';
import type { PaymentMethod } from '@ophelos/shared/constants/payment-methods';
import { ChevronLeft, ChevronRight, CircleAlert, Loader2 } from 'lucide-react';
import { BottomActionBar } from '@/components/layout/BottomActionBar';
import { paymentMethods } from '@/components/payments/payment-method-constants';
import { PaymentMethodSelectionSimple } from '@/components/payments/PaymentMethodSelectionSimple';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';
import { useTranslation } from '@/hooks/useTranslation';
import type { PaymentPlanData } from './PaymentPlanTab';

interface PaymentPlanMethodStepProps {
  plan: PaymentPlanData;
  currency: string;
  availableMethods: PaymentMethod[];
  selectedMethod: PaymentMethod | null;
  onSelectMethod: (method: PaymentMethod) => void;
  /** Methods listed but not yet selectable for this start date, with the reason shown on their row. */
  disabledReasons?: Partial<Record<PaymentMethod, string>>;
  onBack: () => void;
  /** Card goes on to review; every other method sets the plan up from here. */
  onContinue: () => void;
  isProcessing: boolean;
  error: string | null;
}

export function PaymentPlanMethodStep({
  plan,
  currency,
  availableMethods,
  selectedMethod,
  onSelectMethod,
  disabledReasons,
  onBack,
  onContinue,
  isProcessing,
  error,
}: PaymentPlanMethodStepProps) {
  const { t } = useTranslation();
  const selected = paymentMethods.find((m) => m.value === selectedMethod);
  const canContinue = !!selected && !disabledReasons?.[selected.value];

  return (
    <div className="w-full h-full stack items-center mt-4 px-2 xs:px-4">
      <div className="w-full max-w-lg mx-auto h-full flex flex-col space-y-6">
        <div className="flex gap-4 items-center">
          <Button variant="subtle" size="icon-xs" onClick={onBack} className="rounded-full" aria-label={t('common.back')}>
            <ChevronLeft />
          </Button>
          <h2 className="text-lg font-semibold">{t('paymentMethod.selectMethod')}</h2>
        </div>

        <PaymentMethodSelectionSimple
          selectedMethod={selectedMethod}
          onSelect={onSelectMethod}
          disabled={isProcessing}
          availableMethods={availableMethods}
          disabledReasons={disabledReasons}
        />

        <div className="flex items-center justify-center flex-col bg-soft border rounded-2xl px-4 py-2 w-full">
          <div className="text-sm text-muted-foreground">{t('paymentPlan.youllPay')}</div>
          <CurrencyDisplay amount={plan.paymentAmount} currency={currency} className="text-2xl font-semibold" centsClassName="text-lg" />
          <div className="text-sm font-medium">
            {plan.paymentCount} {t('paymentPlan.payments')} {t(`paymentPlan.${plan.frequency}`)}
          </div>
        </div>

        {error && (
          <Alert variant="destructive">
            <CircleAlert />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </div>
      <BottomActionBar>
        <Button onClick={onContinue} size="xl" className="group w-full max-w-sm" disabled={isProcessing || !canContinue}>
          {isProcessing ? (
            <>
              <Loader2 className="size-5 mr-2 animate-spin" />
              {t('verify.processing')}
            </>
          ) : (
            <>
              {selected?.value === 'card'
                ? t('paymentPlan.reviewPlan')
                : selected
                  ? t('paymentPlan.setupPlanWith', { method: t(selected.labelKey) })
                  : t('paymentPlan.setupPayment')}
              <ChevronRight className="size-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </>
          )}
        </Button>
      </BottomActionBar>
    </div>
  );
}
